import React, { Dispatch, Reducer, createContext, useContext, useEffect, useReducer } from "react";

import { getSessionStorage, setSessionStorage } from "../utils/local-storage";

export enum AppContextActionType {
  Theme = "THEME",
  Title = "TITLE",
  Nav = "NAV",
}

export type AppContextAction = {
  type: AppContextActionType;
  payload: any;
};

export class AppContextNavState {
  constructor(public pin: boolean, public expand: boolean, public hover: boolean) {}

  get open() {
    return this.pin || this.expand || this.hover;
  }

  toJSON() {
    return { pin: this.pin, expand: this.expand, hover: this.hover };
  }
}

export type AppContextState = {
  theme: string;
  title: string;
  nav: AppContextNavState;
};

export type AppContextValue = {
  state: AppContextState;
  dispatch: Dispatch<AppContextAction>;
};

export const defaultState: AppContextState = {
  theme: "light",
  title: "Reclaim – Defend your life from work",
  nav: new AppContextNavState(true, false, false),
};

export const appContextReducer: Reducer<AppContextState, AppContextAction> = (state, action) => {
  switch (action.type) {
    case AppContextActionType.Theme:
      return { ...state, theme: action.payload };
    case AppContextActionType.Title:
      const title = action.payload ? `${action.payload} | Reclaim` : defaultState.title;
      document.title = title;
      return { ...state, title };
    case AppContextActionType.Nav:
      const next = {
        ...state.nav.toJSON(),
        ...action.payload,
      };
      return { ...state, nav: new AppContextNavState(next.pin, next.expand, next.hover) };
    default:
      console.warn(`Unhandled action type: ${action.type}`);
      return state;
  }
};

const loadState = () => {
  const persisted = getSessionStorage("app", defaultState);
  return {
    ...persisted,
    nav: new AppContextNavState(persisted.nav.pin, persisted.nav.expand, persisted.nav.hover),
    title: defaultState.title,
  };
};

export const AppContext = createContext<AppContextValue>({
  state: loadState(),
  dispatch: (value) => console.error("Dispatch called before initialized", value),
});

export function useAppContext() {
  return useContext<AppContextValue>(AppContext);
}

export const AppContextProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(appContextReducer, loadState());

  useEffect(() => {
    const persist = Object.entries(state).reduce((acc: { [key: string]: any }, [key, val]) => {
      if (key !== "title") acc[key] = val;
      return acc;
    }, {});
    setSessionStorage("app", persist);
  }, [state]);

  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
};

export default AppContext;
